import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Radio, AlertCircle } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../auth";
import { Avatar } from "../components/cards";
import { SignInNotice } from "./Account";

const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-white/10 bg-white/[0.02] p-6 ${className}`}>{children}</div>
);

const getYouTubeId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|live\/|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  if (m) return m[1];
  if (/^[A-Za-z0-9_-]{11}$/.test(url)) return url;
  return null;
};

export const GoLive = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!user) return <SignInNotice what="go live" />;
  if (!(user.isAdmin || user.isSuperAdmin)) {
    return (
      <div className="mx-auto max-w-md py-24 text-center" data-testid="golive-forbidden">
        <Radio size={40} className="mx-auto text-slate-700" />
        <h1 className="mt-4 font-heading text-xl font-bold text-white">Admins only</h1>
        <p className="mt-2 text-sm text-slate-500">Only admins can start a live broadcast.</p>
      </div>
    );
  }

  const ytId = getYouTubeId(url);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!title.trim()) {
      setError("Please add a title for your stream.");
      return;
    }
    if (!ytId) {
      setError("Please paste a valid YouTube live or video link.");
      return;
    }
    setBusy(true);
    try {
      const s = await api.post("/live/streams", { title: title.trim(), youtubeUrl: url.trim() });
      navigate(`/app/live/${s.id}`);
    } catch (err) {
      setError(err.message || "Could not start the stream.");
    }
    setBusy(false);
  };

  return (
    <div className="mx-auto max-w-2xl" data-testid="golive-page">
      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-crimson/15 text-crimson"><Radio size={20} /></span>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-white">Go Live</h1>
          <p className="text-sm text-slate-500">Start a live broadcast — it appears on the web and in the mobile app.</p>
        </div>
      </div>

      <Card>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Stream title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What are you streaming?"
              data-testid="golive-title"
              className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder-slate-600 outline-none focus:border-crimson/60"
            />
          </div>
          <div>
            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-slate-500">YouTube live link</label>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.youtube.com/live/… or https://youtu.be/…"
              data-testid="golive-url"
              className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder-slate-600 outline-none focus:border-crimson/60"
            />
            <p className="mt-2 text-xs leading-relaxed text-slate-500">
              Michael C Streamline broadcasts through YouTube Live. Start your stream in YouTube, then paste its link here — the same way as in the mobile app.
            </p>
          </div>

          {ytId && (
            <div className="overflow-hidden rounded-xl border border-white/10" data-testid="golive-preview">
              <img src={`https://i.ytimg.com/vi/${ytId}/hqdefault.jpg`} alt="Stream preview" className="aspect-video w-full object-cover" />
              <p className="bg-white/[0.03] px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest text-slate-400">Preview · {ytId}</p>
            </div>
          )}

          {error && (
            <p className="flex items-center gap-2 rounded-xl border border-crimson/30 bg-crimson/10 px-4 py-3 text-sm text-crimson" data-testid="golive-error">
              <AlertCircle size={15} /> {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="golive-start"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-crimson py-3.5 font-heading text-sm font-bold text-white transition-colors hover:bg-crimson-hover disabled:opacity-50"
          >
            <Radio size={16} /> {busy ? "Starting…" : "Start live broadcast"}
          </button>
        </form>
      </Card>

      <Card className="mt-6">
        <p className="text-sm leading-relaxed text-slate-400">
          Viewers can watch, comment and react live on both the web and the mobile app. When you end the stream, it moves to Past Live where people can replay it.
        </p>
      </Card>
    </div>
  );
};

export default GoLive;
